import React, { useEffect, useState } from 'react';
import { Table, Tag, Button, Spin, Space, Modal, message, Input } from 'antd';
import { useNavigate } from 'react-router-dom';
import ApiService from '../Components/apiService';
import { BASE_URL ,DateFormat,DateTimeFormat} from '../Components/Constant';
import Cookies from 'js-cookie';
import { convertDateToUserTimezone } from '../Components/Utility';

const ApprovalDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [approvalRequests, setApprovalRequests] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [comment, setComment] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [recordDetails, setRecordDetails] = useState(null);
  const [recordLoading, setRecordLoading] = useState(false);



  const userId = Cookies.get('userId');

  const openDetails = async (record) => {
    setSelectedRecord(record);
    setDetailsModalOpen(true);
    setRecordLoading(true);
    setRecordDetails(null);

    try {
      const apiService = new ApiService(
        `${BASE_URL}/fetch_single_record/${record.object_name}/${record.record_id}`,
        { 'Content-Type': 'application/json' },
        'GET'
      );

      const response = await apiService.makeCall();

      // assuming API returns full record object
      setRecordDetails(response);
    } catch (error) {
      message.error("Failed to load record details");
    } finally {
      setRecordLoading(false);
    }
  };


  const handleApprovalAction = async (action, requestId) => {
    if (!comment.trim()) {
      message.error("Please enter a comment.");
      return;
    }

    setActionLoading(true);
    try {
      const apiService = new ApiService(
        `${BASE_URL}/approvals/update/${requestId}`,
        { "Content-Type": "application/json" },
        "POST",
        {
          approval_request: {
            action,
            comment: comment.trim()
          }
        }
      );

      await apiService.makeCall();

      message.success(`Request ${action === 'approve' ? 'approved' : 'rejected'} successfully.`);

      // Remove from table
      setApprovalRequests(prev =>
        prev.filter(req => req._id !== requestId)
      );

      setDetailsModalOpen(false);
      setComment("");

    } catch (err) {
      message.error("Error performing action: " + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestClick = (record) => {
    navigate(`/record/${record.object_name}/${record.record_id}`); // Adjust URL as needed
  };

  // Columns for the table
  const columns = [
    {
      title: 'Request Name',
      dataIndex: 'step_name',
      key: 'requestName',
      render: (text, record) => (
        <a
          href={`/record/${record.object_name}/${record.record_id}`}
          target="_self"
          style={{ color: '#1890ff', cursor: 'pointer' }}
        >
          {text}
        </a>
      ),
    },
    {
      title: 'Requester Name',
      key: 'requesterName',
      render: (_, record) => record.requested_by?.Name || 'N/A',

    },
    {
      title: 'Object',
      dataIndex: 'object_name',
      key: 'objectName',
    },
    {
      title: "Record Details",
      key: "recordDetails",
      render: (_, record) => (
        <Button
          size="small"
          type="link"
          onClick={() => openDetails(record)}
        >
          View Details
        </Button>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color = 'blue';
        if (status === 'Approved') color = 'green';
        else if (status === 'Pending') color = 'orange';
        else if (status === 'Rejected') color = 'red';
        return <Tag color={color}>{status.toUpperCase()}</Tag>;
      },
    },
  ];

  // Fetch approval requests from the API
  useEffect(() => {
    const fetchApprovalRequests = async () => {
      try {
        const apiService = new ApiService(`${BASE_URL}/approvals/user/${userId}`, {
          'Content-Type': 'application/json',
        }, 'GET');

        const response = await apiService.makeCall();
        const pendingRequests = response.filter(item => item.status === 'Pending');
        // Update with your API endpoint
        setApprovalRequests(pendingRequests);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchApprovalRequests();
  }, []);


  const formatFieldValue = (value) => {
  if (value === null || value === undefined) return "—";

  // Lookup / object
  if (typeof value === "object") {
    return value?.Name ?? "—";
  }

  // Date / DateTime detection (SAFE)
  if (typeof value === "string" && !isNaN(Date.parse(value))) {
    

    // Date only
    return convertDateToUserTimezone(value, DateFormat);
  }

  // Default
  return String(value);
};




  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", width: "100%" }} className="non-draggable">
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Spin spinning={loading} >
          <Table
            columns={columns}
            dataSource={approvalRequests}
            rowKey="requestId"
            pagination={{
              current: currentPage,
              showSizeChanger: false,
              onChange: (page, pageSize) => {
                setCurrentPage(page);
              },
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
            scroll={{ y: 180 }}
          />
        </Spin>
        <Modal
          title="Record Details"
          open={detailsModalOpen}
          onCancel={() => {
            setDetailsModalOpen(false);
            setSelectedRecord(null);
            setRecordDetails(null);
            setComment("");
          }}
          zIndex={1001}

          footer={null}
          width="50%"
        >
          {selectedRecord ? (
            <div style={{ maxHeight: "60vh", overflowY: "auto" }}>

              {/* Record Fields */}
              {/* <h3>Record Details</h3> */}
              <div style={{ marginBottom: 16 }}>
                {recordLoading ? (
                  <Spin />
                ) : recordDetails ? (
                  <div>
                    {Object.keys(selectedRecord.selected_fields || {}).map((fieldName) => {
                      const value = recordDetails[fieldName];

                      return (
                        <div
                          key={fieldName}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "16px",
                            padding: "12px 0",
                            borderBottom: "1px solid #f0f0f0"
                          }}
                        >
                          {/* Field Label */}
                          <div style={{ color: "#6b7280", fontSize: 13 }}>
                            {fieldName}
                          </div>

                          {/* Field Value */}
                          <div style={{ fontWeight: 500 }}>
  {formatFieldValue(recordDetails[fieldName])}
</div>

                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div>No record data found</div>
                )}



              </div>

              <hr />

              {/* Comment Input */}
              <div style={{ marginTop: 16 }}>
                <strong>Comment:</strong>
                <Input.TextArea
                  rows={4}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Enter your comment"
                />
              </div>


              {/* Footer Buttons */}
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>

                {/* View Record Button */}
                <a
                  href={`/record/${selectedRecord.object_name}/${selectedRecord.record_id}`}
                  target="_self"
                >
                  <Button type="default">View Full Record</Button>
                </a>

                <Space>
                  {/* Approve Button */}
                  <Button
                    type="primary"
                    loading={actionLoading}
                    onClick={() => handleApprovalAction("approve", selectedRecord._id)}
                  >
                    Approve
                  </Button>

                  {/* Reject Button */}
                  <Button
                    danger
                    loading={actionLoading}
                    onClick={() => handleApprovalAction("reject", selectedRecord._id)}
                  >
                    Reject
                  </Button>
                </Space>

              </div>

            </div>
          ) : null}
        </Modal>


      </div>
    </div>
  );
};

export default ApprovalDashboard;
